import type { TransportStage } from "src/api/client-api/transport/transportStages";
import type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportDraft,
    IngredientSupplierTransportPersistenceStatus,
    IngredientSupplierTransportSelection,
} from "./ingredientSupplierTransportTypes";

export type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportDraft,
    IngredientSupplierTransportPersistenceStatus,
    IngredientSupplierTransportSelection,
} from "./ingredientSupplierTransportTypes";

export class IngredientSupplierTransportContractError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "IngredientSupplierTransportContractError";
    }
}

export const shouldPersistIngredientSupplierTransportSelections = (row: IngredientSupplierTransportDraft): boolean =>
    row.transportSelectionsStatus === "new" || row.transportSelectionsStatus === "dirty" || row["@id"] === undefined;

export const getNextIngredientSupplierTransportStatus = (
    currentStatus: IngredientSupplierTransportPersistenceStatus,
    reason: IngredientSupplierTransportChangeReason,
): IngredientSupplierTransportPersistenceStatus => {
    if (currentStatus === "new") return "new";
    if (reason === "user") return "dirty";
    if (currentStatus === "dirty") return "dirty";
    return "clean";
};

export const hasMissingRequiredIngredientSupplierTransportSelections = (
    rows: ReadonlyArray<IngredientSupplierTransportDraft>,
    enabledStages: ReadonlyArray<TransportStage>,
): boolean =>
    rows.some(
        (row) =>
            shouldPersistIngredientSupplierTransportSelections(row) &&
            enabledStages.some((stage) => row.transportSelections?.[stage]?.transportCircuit == null),
    );

type PersistedIngredientSupplierRelation = {
    "@id"?: IRI;
};

type ReconcileOptions<
    TDraft extends IngredientSupplierTransportDraft,
    TPersisted extends PersistedIngredientSupplierRelation,
> = {
    rows: ReadonlyArray<TDraft>;
    persistedRows: ReadonlyArray<TPersisted>;
    getDraftCounterpartIri: (row: TDraft) => IRI;
    getPersistedCounterpartIri: (row: TPersisted) => IRI | undefined;
};

export const reconcilePersistedIngredientSupplierRelations = <
    TDraft extends IngredientSupplierTransportDraft,
    TPersisted extends PersistedIngredientSupplierRelation,
>({
    rows,
    persistedRows,
    getDraftCounterpartIri,
    getPersistedCounterpartIri,
}: ReconcileOptions<TDraft, TPersisted>): TDraft[] => {
    const resolvedRelationIris = new Set<IRI>();

    return rows.map((row) => {
        const existingRelationIri = row["@id"];
        if (existingRelationIri !== undefined) {
            const matches = persistedRows.filter((persistedRow) => persistedRow["@id"] === existingRelationIri);
            if (matches.length !== 1) {
                throw new IngredientSupplierTransportContractError(
                    `Expected persisted ingredient-supplier relation ${existingRelationIri}; received ${matches.length}.`,
                );
            }

            const draftCounterpartIri = getDraftCounterpartIri(row);
            const persistedCounterpartIri = getPersistedCounterpartIri(matches[0]);
            if (persistedCounterpartIri !== undefined && persistedCounterpartIri !== draftCounterpartIri) {
                throw new IngredientSupplierTransportContractError(
                    `Persisted ingredient-supplier relation ${existingRelationIri} targets an unexpected resource.`,
                );
            }

            if (resolvedRelationIris.has(existingRelationIri)) {
                throw new IngredientSupplierTransportContractError(
                    `Duplicate ingredient-supplier relation ${existingRelationIri}.`,
                );
            }
            resolvedRelationIris.add(existingRelationIri);
            return row;
        }

        const counterpartIri = getDraftCounterpartIri(row);
        const matches = persistedRows.filter(
            (persistedRow) =>
                persistedRow["@id"] !== undefined && getPersistedCounterpartIri(persistedRow) === counterpartIri,
        );

        if (matches.length !== 1 || matches[0]["@id"] === undefined) {
            throw new IngredientSupplierTransportContractError(
                `Expected one persisted ingredient-supplier relation for ${counterpartIri}; received ${matches.length}.`,
            );
        }

        const relationIri = matches[0]["@id"];
        if (resolvedRelationIris.has(relationIri)) {
            throw new IngredientSupplierTransportContractError(
                `Duplicate ingredient-supplier relation ${relationIri}.`,
            );
        }
        resolvedRelationIris.add(relationIri);

        return { ...row, "@id": relationIri };
    });
};

type IngredientSupplierTransportSave = (
    relationIri: IRI,
    selections?: IngredientSupplierTransportSelection,
) => Promise<IngredientSupplierTransportSelection | undefined>;

export type IngredientSupplierTransportSaveSuccess = {
    relationIri: IRI;
    submittedSelections?: IngredientSupplierTransportSelection;
    savedSelections?: IngredientSupplierTransportSelection;
};

export type IngredientSupplierTransportSaveFailure = {
    relationIri: IRI;
    submittedSelections?: IngredientSupplierTransportSelection;
    error: unknown;
};

export const applyIngredientSupplierTransportSaveSuccesses = <TRow extends IngredientSupplierTransportDraft>(
    rows: ReadonlyArray<TRow>,
    successes: ReadonlyArray<IngredientSupplierTransportSaveSuccess>,
): TRow[] =>
    rows.map((row) => {
        const success = successes.find((candidate) => candidate.relationIri === row["@id"]);
        if (success === undefined || row.transportSelections !== success.submittedSelections) {
            return row;
        }

        return {
            ...row,
            transportSelections: success.savedSelections,
            transportSelectionsStatus: "clean",
        };
    });

export const persistIngredientSupplierTransportSelections = async (
    rows: ReadonlyArray<IngredientSupplierTransportDraft>,
    save: IngredientSupplierTransportSave,
): Promise<{
    successes: IngredientSupplierTransportSaveSuccess[];
    failures: IngredientSupplierTransportSaveFailure[];
}> => {
    const candidates = rows.filter(shouldPersistIngredientSupplierTransportSelections).map((row) => {
        const relationIri = row["@id"];
        if (relationIri === undefined) {
            throw new IngredientSupplierTransportContractError(
                "Cannot persist transport selections without an ingredient-supplier relation IRI.",
            );
        }

        return {
            relationIri,
            submittedSelections: row.transportSelections,
        };
    });

    const candidateRelationIris = new Set<IRI>();
    for (const candidate of candidates) {
        if (candidateRelationIris.has(candidate.relationIri)) {
            throw new IngredientSupplierTransportContractError(
                `Duplicate transport persistence candidate ${candidate.relationIri}.`,
            );
        }
        candidateRelationIris.add(candidate.relationIri);
    }

    const settled = await Promise.allSettled(
        candidates.map(async (candidate) => ({
            ...candidate,
            savedSelections:
                (await save(candidate.relationIri, candidate.submittedSelections)) ?? candidate.submittedSelections,
        })),
    );

    const successes: IngredientSupplierTransportSaveSuccess[] = [];
    const failures: IngredientSupplierTransportSaveFailure[] = [];

    settled.forEach((result, index) => {
        if (result.status === "fulfilled") {
            successes.push(result.value);
            return;
        }

        failures.push({
            ...candidates[index],
            error: result.reason,
        });
    });

    return { successes, failures };
};
